import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'

// antd
import {
    Typography,
    Avatar,
    Button,
    Modal,
    Select,
    message,
    Dropdown,
    Popover,
    Tooltip,
} from 'antd'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'

// slices
import {
    taskListSupport,
    taskById,
    mainTaskList,
    mainTaskById,
} from '../taskRefactored/tasks.slice'
import {
    usersListForClientModal,
    usersState,
    resetClientModalUsers,
    setClientModalUserSearch,
} from '../users/users.slice'
import { loginState } from '../login/login.slice'
import {
    fetchProjectDetail,
    projectsState,
    updateProject,
} from './projects.slice'
import {
    clearWidgetSession,
    getWidgetProjectId,
    isMongoObjectId,
    refreshWidgetTokenFromApi,
    resolveWidgetCodeForRoute,
} from '../../helpers/widgetAuth'
import {
    assignUserToProject,
    unAssignUserToProject,
    getProjectByWidgetCode,
} from './projects.service'
import { getProjectPermissions } from '../clients/clients.service'
import {
    ProjectPermissionsContext,
    resolveModulePermission,
    PROJECT_MODULE_SUPPORT,
} from './projectPermissions'
import { loadDevelopmentSprintBoard } from './developmentSprint.slice'
import {
    buildDevelopmentSprintListQuery,
    buildMainTaskListRequest,
} from '../taskRefactored/tasks.service'

// components
import ImportTasksModal from './components/ImportTasksModal'
import { showActionNotification } from '../../components/notification'
import { getClientsPermission } from '../../helpers/reportsPermission'
import ProjectColorIconFlyout from './components/ProjectColorIconFlyout'
import CreateProjectModal from './components/CreateProjectModal'
import CreateClientModal from '../clients/components/CreateClientModal'
import ProjectAccessDeniedView from './components/ProjectAccessDeniedView'
import ProjectMainTabs from './components/ProjectMainTabs'
import ProjectMainContent from './components/ProjectMainContent'
import ProjectTaskDrawer from './components/ProjectTaskDrawer'

// constants
import {
    PRIVATE_ROUTES,
    buildProjectTaskTabSearch,
    buildProjectWidgetSearch,
    isProjectWidgetMode,
    PROJECT_TASK_TAB_DEVELOPMENT,
    PROJECT_TASK_TAB_QUERY,
} from '../../routes'
import { DEFAULT_COLOR_PALETTE } from '../../constants/projects'
import { harmonicIconToneForBackground } from '../../helpers/harmonicIconColor'

// assets
import { CloseModalIcon, CrossIcon } from '../../assets/icons'
import { getRandomAvatar } from '../../assets/clients/avatar'

// css
import styles from './projectDetail.module.css'

const { Title, Text } = Typography
const SUPPORT_VIEW_ROUTES = ['board', 'list', 'roadmap', 'sprint']
const DEVELOPMENT_VIEW_ROUTES = ['board', 'list', 'roadmap', 'sprint']
const TOP_LEVEL_VIEW_ROUTES = [
    'files',
    'reports',
    'storyMap',
    'prototype',
    'testrun',
]

const DEVELOPMENT_VIEW_TABS = [
    {
        key: 'board',
        label: 'Board',
        icon: 'si:bar-chart-line',
        iconStyle: { transform: 'rotate(-180deg)' },
    },
    { key: 'list', label: 'List', icon: 'carbon:list-boxes' },
    { key: 'roadmap', label: 'Roadmap', icon: 'carbon:roadmap' },
    { key: 'sprint', label: 'Sprint', icon: 'mdi:run-fast' },
]

const SUPPORT_VIEW_TABS = DEVELOPMENT_VIEW_TABS.filter(
    (tab) => tab.key !== 'sprint'
)

/** Merge toolbar filter updates and drop cleared fields (null / empty). */
const mergeToolbarFilter = (prev = {}, next = {}) => {
    const merged = { ...prev, ...next }

    if (Object.prototype.hasOwnProperty.call(next, 'type') && !next.type) {
        delete merged.type
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'selectedType') &&
        (!Array.isArray(next.selectedType) || next.selectedType.length === 0)
    ) {
        merged.selectedType = []
        delete merged.type
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'isCompleted') &&
        typeof next.isCompleted !== 'boolean'
    ) {
        delete merged.isCompleted
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'mineAssignee') &&
        !next.mineAssignee
    ) {
        delete merged.mineAssignee
    }

    return merged
}

const resolveProjectDetailRecord = (detail) => {
    if (!detail || typeof detail !== 'object') return null
    if (detail._id && detail.success !== true) return detail
    if (detail.result?._id) return detail.result
    return detail._id ? detail : null
}

const readStoryMapModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.storymap ?? modulePermissions?.storyMap ?? null
}

const readStoryMapPermissionFromListResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    const items = Array.isArray(result) ? result : [result]
    const match = projectId
        ? items.find((item) => String(item?.projectId) === String(projectId))
        : items[0]

    const permissions = match?.permissions
    return permissions?.storymap ?? permissions?.storyMap ?? null
}

const readTestRunModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.test ?? modulePermissions?.testrun ?? null
}

const readDevelopmentModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.development ?? null
}

const readTestRunPermissionFromListResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    const items = Array.isArray(result) ? result : [result]
    const match = projectId
        ? items.find((item) => String(item?.projectId) === String(projectId))
        : items[0]

    const permissions = match?.permissions
    return permissions?.test ?? permissions?.testrun ?? null
}

const readPrototypeModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.prototypes ?? modulePermissions?.prototype ?? null
}

const readPrototypePermissionFromListResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    const items = Array.isArray(result) ? result : [result]
    const match = projectId
        ? items.find((item) => String(item?.projectId) === String(projectId))
        : items[0]

    const permissions = match?.permissions
    return permissions?.prototypes ?? permissions?.prototype ?? null
}

const TASK_DETAIL_DRAWER_MIN_WIDTH = 380
const TASK_DETAIL_DRAWER_MAX_WIDTH = 900

const getDefaultTaskDetailDrawerWidth = () => {
    if (typeof window === 'undefined') return 560
    return Math.min(Math.max(Math.round(window.innerWidth * 0.44), 460), 700)
}

const copyTextToClipboard = async (text) => {
    if (!text) return false
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
    }
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
        return document.execCommand('copy')
    } finally {
        document.body.removeChild(ta)
    }
}

const readTaskTabQuery = (search) =>
    new URLSearchParams(search || '').get(PROJECT_TASK_TAB_QUERY)

const mergeProjectRouteSearch = (
    isWidgetMode,
    widgetSearch,
    extraSearch = ''
) => {
    if (!isWidgetMode) return extraSearch
    if (!extraSearch) return widgetSearch
    const params = new URLSearchParams(
        extraSearch.startsWith('?') ? extraSearch.slice(1) : extraSearch
    )
    params.set('mode', 'widget')
    return `?${params.toString()}`
}

const resolveProjectRouteSearch = (
    isWidgetMode,
    widgetSearch,
    locationSearch = ''
) => (isWidgetMode ? widgetSearch : locationSearch || '')

const buildProjectViewPath = (routeId, viewName, taskId) =>
    taskId
        ? PRIVATE_ROUTES.projects.projectIdViewTask(routeId, viewName, taskId)
        : PRIVATE_ROUTES.projects.projectIdView(routeId, viewName)

const ProjectDetail = () => {
    const { id: routeId, view, taskId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const isWidgetMode = useMemo(
        () => isProjectWidgetMode(location.search),
        [location.search]
    )
    const widgetSearch = isWidgetMode
        ? buildProjectWidgetSearch(
              resolveWidgetCodeForRoute(routeId, location.search)
          )
        : ''

    const { projectDetail, projectDetailStatus, projectAccessDeniedMessage } =
        useSelector(
            (state) => {
                const projects = projectsState(state)
                return {
                    projectDetail: projects.projectDetail,
                    projectDetailStatus: projects.projectDetailStatus,
                    projectAccessDeniedMessage:
                        projects.projectAccessDeniedMessage,
                }
            },
            shallowEqual
        )

    const [widgetAuthStatus, setWidgetAuthStatus] = useState(() =>
        isProjectWidgetMode(
            typeof window !== 'undefined' ? window.location.search : ''
        )
            ? 'loading'
            : 'idle'
    )
    const [widgetResolvedProjectId, setWidgetResolvedProjectId] = useState(null)

    const projectId = useMemo(() => {
        if (!isWidgetMode) return routeId
        return (
            widgetResolvedProjectId ??
            getWidgetProjectId() ??
            (isMongoObjectId(routeId) ? routeId : null) ??
            projectDetail?._id ??
            null
        )
    }, [isWidgetMode, routeId, widgetResolvedProjectId, projectDetail?._id])
    const {
        clientModalUsersListData = [],
        clientModalUserPage,
        clientModalUserSearch,
        clientModalUserIsNext,
        getUserStatus,
    } = useSelector(
        (state) => {
            const users = usersState(state)
            return {
                clientModalUsersListData:
                    users.clientModalUsersListData || [],
                clientModalUserPage: users.clientModalUserPage,
                clientModalUserSearch: users.clientModalUserSearch,
                clientModalUserIsNext: users.clientModalUserIsNext,
                getUserStatus: users.getUserStatus,
            }
        },
        shallowEqual
    )

    const { userProfile, loginStatus } = useSelector(
        (state) => {
            const login = loginState(state)
            return {
                userProfile: login.userProfile,
                loginStatus: login.status,
            }
        },
        shallowEqual
    )

    const [isMemberModalVisible, setIsMemberModalVisible] =
        React.useState(false)
    const [inviteEmail, setInviteEmail] = React.useState([])
    const [selectedMap, setSelectedMap] = React.useState({})
    const [isInviting, setIsInviting] = useState(false)
    const [userSearchValue, setUserSearchValue] = useState('')
    const [selectedTaskId, setSelectedTaskId] = useState(() => taskId || null)
    const selectedTaskIdRef = useRef(taskId || null)
    const [taskDetailVisible, setTaskDetailVisible] = useState(() =>
        Boolean(taskId)
    )
    // Ignore outside-click close briefly after deep-link open (My Tasks, search, etc.)
    const ignoreTaskDrawerOutsideClickRef = useRef(Boolean(taskId))
    const [taskDetailFullscreen, setTaskDetailFullscreen] = useState(false)
    // 'idle' | 'entering' | 'exiting'
    const [fullscreenAnimState, setFullscreenAnimState] = useState('idle')
    const [scrollToSection, setScrollToSection] = useState(null)
    const [filteredAssignees] = useState([])
    const [taskListSearchTerm, setTaskListSearchTerm] = useState('')
    /**
     * True while an *intentional* task-list fetch is in flight — opening the
     * tab, changing a filter, or running a search. Background syncs after a
     * mutation pass `silent: true` so they never blank out optimistic updates.
     */
    const [showTaskListRefreshOverlay, setShowTaskListRefreshOverlay] =
        useState(false)
    const taskListOverlaySeqRef = useRef(0)

    const trackToolbarQuery = useCallback((promise, silent) => {
        if (silent) return promise
        const seq = ++taskListOverlaySeqRef.current
        setShowTaskListRefreshOverlay(true)
        Promise.resolve(promise)
            .catch(() => {})
            .finally(() => {
                // Ignore a stale request that resolves after a newer one.
                if (taskListOverlaySeqRef.current === seq) {
                    setShowTaskListRefreshOverlay(false)
                }
            })
        return promise
    }, [])
    const [supportListQuery, setSupportListQuery] = useState({
        isCompleted: null,
        mineAssignee: null,
    })
    const taskDetailDrawerRef = useRef(null)
    const [projectMenuOpen, setProjectMenuOpen] = useState(false)
    const [colorIconPopoverOpen, setColorIconPopoverOpen] = useState(false)
    const [avatarColorIconPopoverOpen, setAvatarColorIconPopoverOpen] =
        useState(false)
    const [importModalVisible, setImportModalVisible] = useState(false)
    const [editProjectOpen, setEditProjectOpen] = useState(false)
    const [projectClientsPopoverOpen, setProjectClientsPopoverOpen] =
        useState(false)
    const [isClientModalOpen, setIsClientModalOpen] = useState(false)
    const [clientModalMode, setClientModalMode] = useState('create')
    const [editingClient, setEditingClient] = useState(null)
    const addTaskToFirstSwimlaneRef = useRef(null)
    const developmentToolbarFilterRef = useRef({})
    const supportToolbarFilterRef = useRef({})
    const [activeMainTab, setActiveMainTab] = useState(() =>
        readTaskTabQuery(location.search) === PROJECT_TASK_TAB_DEVELOPMENT
            ? 'development'
            : 'support'
    )
    const [supportSubView, setSupportSubView] = useState(() =>
        SUPPORT_VIEW_ROUTES.includes(view) ? view : 'board'
    )
    const [developmentSubView, setDevelopmentSubView] = useState(() =>
        DEVELOPMENT_VIEW_ROUTES.includes(view) ? view : 'board'
    )
    const [liveTaskTitlePatch, setLiveTaskTitlePatch] = useState(null)
    const [taskDetailDrawerWidth, setTaskDetailDrawerWidth] = useState(
        getDefaultTaskDetailDrawerWidth
    )
    const taskDetailMainContentRef = useRef(null)
    const taskDetailDrawerWidthRef = useRef(getDefaultTaskDetailDrawerWidth())
    const taskDetailResizeRef = useRef({ startX: 0, startWidth: 560 })
    const taskDetailResizeRafRef = useRef(null)
    const taskDetailPendingWidthRef = useRef(null)
    const developmentPermission = useMemo(
        () => readDevelopmentModulePermission(projectDetail),
        [projectDetail]
    )

    const canAccessDevelopmentTab = useMemo(() => {
        if (isWidgetMode) return false
        if (userProfile?.isSuperAdmin || userProfile?.isExistingUser)
            return true
        if (developmentPermission) return Boolean(developmentPermission.isRead)
        return false
    }, [
        isWidgetMode,
        userProfile?.isSuperAdmin,
        userProfile?.isExistingUser,
        developmentPermission,
    ])

    const canCopyWidgetLink = useMemo(
        () =>
            !isWidgetMode &&
            Boolean(userProfile?.isSuperAdmin || userProfile?.isExistingUser),
        [isWidgetMode, userProfile?.isSuperAdmin, userProfile?.isExistingUser]
    )

    const canBypassStoryMapPermissions = useMemo(
        () => Boolean(userProfile?.isSuperAdmin),
        [userProfile?.isSuperAdmin]
    )

    const isClientUser = useMemo(
        () =>
            Boolean(
                userProfile?.isSupportUser &&
                !userProfile?.isExistingUser &&
                !userProfile?.isSuperAdmin
            ),
        [
            userProfile?.isSupportUser,
            userProfile?.isExistingUser,
            userProfile?.isSuperAdmin,
        ]
    )

    const projectModulePermissions = useMemo(() => {
        // Widget embeds are view-only (same as isRead without isEdit/isDelete).
        if (isWidgetMode) {
            return {
                [PROJECT_MODULE_SUPPORT]: {
                    isRead: true,
                    isEdit: false,
                    isDelete: false,
                },
            }
        }
        const record = resolveProjectDetailRecord(projectDetail)
        return record?.supportProjectPermission ?? null
    }, [isWidgetMode, projectDetail])

    const projectPermissionsContextValue = useMemo(
        () => ({
            // Never bypass permissions in widget mode — keep it read-only.
            isSuperAdmin: isWidgetMode
                ? false
                : Boolean(userProfile?.isSuperAdmin),
            modules: projectModulePermissions,
        }),
        [isWidgetMode, userProfile?.isSuperAdmin, projectModulePermissions]
    )

    const supportPermission = useMemo(
        () =>
            resolveModulePermission(
                PROJECT_MODULE_SUPPORT,
                projectPermissionsContextValue
            ),
        [projectPermissionsContextValue]
    )
    const canViewSupportTab = supportPermission.canView

    const [clientStoryMapPermission, setClientStoryMapPermission] =
        useState(null)
    const [clientTestRunPermission, setClientTestRunPermission] = useState(null)
    const [clientPrototypePermission, setClientPrototypePermission] =
        useState(null)
    const [
        clientStoryMapPermissionsResolved,
        setClientStoryMapPermissionsResolved,
    ] = useState(false)

    const storyMapPermissionFromDetail = useMemo(
        () => readStoryMapModulePermission(projectDetail),
        [projectDetail]
    )

    const testRunPermissionFromDetail = useMemo(
        () => readTestRunModulePermission(projectDetail),
        [projectDetail]
    )

    const prototypePermissionFromDetail = useMemo(
        () => readPrototypeModulePermission(projectDetail),
        [projectDetail]
    )

    const prototypePermissionFromProfile = useMemo(() => {
        const modules = userProfile?.supportProjectPermission
        if (!modules || typeof modules !== 'object') return null
        return modules.prototypes ?? modules.prototype ?? null
    }, [userProfile?.supportProjectPermission])

    const storyMapPermission =
        storyMapPermissionFromDetail ?? clientStoryMapPermission

    const testRunPermission =
        testRunPermissionFromDetail ?? clientTestRunPermission

    const prototypePermission =
        prototypePermissionFromDetail ??
        clientPrototypePermission ??
        prototypePermissionFromProfile

    useEffect(() => {
        setClientStoryMapPermissionsResolved(false)
        setClientStoryMapPermission(null)
        setClientTestRunPermission(null)
        setClientPrototypePermission(null)
    }, [projectId])

    useEffect(() => {
        if (canBypassStoryMapPermissions || !isClientUser || !projectId) {
            setClientStoryMapPermission(null)
            setClientTestRunPermission(null)
            setClientPrototypePermission(null)
            setClientStoryMapPermissionsResolved(true)
            return undefined
        }

        const needsStoryMapFetch = !storyMapPermissionFromDetail
        const needsTestRunFetch = !testRunPermissionFromDetail
        const needsPrototypeFetch = !prototypePermissionFromDetail

        if (!needsStoryMapFetch && !needsTestRunFetch && !needsPrototypeFetch) {
            setClientStoryMapPermission(null)
            setClientTestRunPermission(null)
            setClientPrototypePermission(null)
            setClientStoryMapPermissionsResolved(true)
            return undefined
        }

        if (projectDetailStatus !== 'loaded') {
            setClientStoryMapPermissionsResolved(false)
            return undefined
        }

        const supportUserId = userProfile?._id
        if (!supportUserId) {
            setClientStoryMapPermissionsResolved(false)
            return undefined
        }

        let cancelled = false

        ;(async () => {
            try {
                const { data } = await getProjectPermissions(
                    supportUserId,
                    projectId
                )
                if (cancelled) return

                if (needsStoryMapFetch) {
                    const permission = readStoryMapPermissionFromListResponse(
                        data,
                        projectId
                    )
                    setClientStoryMapPermission(permission)
                }

                if (needsTestRunFetch) {
                    const permission = readTestRunPermissionFromListResponse(
                        data,
                        projectId
                    )
                    setClientTestRunPermission(permission)
                }

                if (needsPrototypeFetch) {
                    const permission = readPrototypePermissionFromListResponse(
                        data,
                        projectId
                    )
                    setClientPrototypePermission(permission)
                }
            } finally {
                if (!cancelled) {
                    setClientStoryMapPermissionsResolved(true)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        canBypassStoryMapPermissions,
        isClientUser,
        projectId,
        projectDetailStatus,
        storyMapPermissionFromDetail,
        testRunPermissionFromDetail,
        prototypePermissionFromDetail,
        userProfile?._id,
    ])

    const isStoryMapAccessPending = useMemo(() => {
        if (isWidgetMode) return false
        if (loginStatus === 'loading') return true
        if (canBypassStoryMapPermissions) return false
        if (storyMapPermission) return false

        const record = resolveProjectDetailRecord(projectDetail)
        const isProjectReady =
            projectDetailStatus === 'loaded' &&
            record?._id &&
            String(record._id) === String(projectId)

        if (projectDetailStatus === 'loading' || !isProjectReady) return true
        if (isClientUser && !clientStoryMapPermissionsResolved) return true

        return false
    }, [
        isWidgetMode,
        loginStatus,
        canBypassStoryMapPermissions,
        storyMapPermission,
        projectDetail,
        projectDetailStatus,
        projectId,
        isClientUser,
        clientStoryMapPermissionsResolved,
    ])

    const isPrototypeAccessPending = useMemo(() => {
        if (isWidgetMode) return false
        if (loginStatus === 'loading') return true
        if (canBypassStoryMapPermissions) return false
        if (prototypePermission) return false

        const record = resolveProjectDetailRecord(projectDetail)
        const isProjectReady =
            projectDetailStatus === 'loaded' &&
            record?._id &&
            String(record._id) === String(projectId)

        if (projectDetailStatus === 'loading' || !isProjectReady) return true
        if (isClientUser && !clientStoryMapPermissionsResolved) return true

        return false
    }, [
        isWidgetMode,
        loginStatus,
        canBypassStoryMapPermissions,
        prototypePermission,
        projectDetail,
        projectDetailStatus,
        projectId,
        isClientUser,
        clientStoryMapPermissionsResolved,
    ])

    const canViewStoryMap = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (storyMapPermission) return Boolean(storyMapPermission.isRead)
        return false
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const canAccessStoryMapRoute = canViewStoryMap || isStoryMapAccessPending

    const canViewPrototype = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (prototypePermission) return Boolean(prototypePermission.isRead)
        return false
    }, [canBypassStoryMapPermissions, prototypePermission])

    const canAccessPrototypeRoute = canViewPrototype || isPrototypeAccessPending

    const canViewTestRun = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (testRunPermission) return Boolean(testRunPermission.isRead)
        if (projectDetailStatus === 'loading') return false
        if (!resolveProjectDetailRecord(projectDetail)?._id) return false
        return false
    }, [
        canBypassStoryMapPermissions,
        projectDetail,
        projectDetailStatus,
        testRunPermission,
    ])

    const storyMapReadOnly = useMemo(() => {
        if (canBypassStoryMapPermissions) return false
        if (!storyMapPermission) return true
        return !Boolean(storyMapPermission.isEdit)
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const storyMapCanDelete = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (!storyMapPermission) return false
        return Boolean(storyMapPermission.isDelete)
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const prototypeReadOnly = useMemo(() => {
        if (canBypassStoryMapPermissions) return false
        if (!prototypePermission) return true
        return !Boolean(prototypePermission.isEdit)
    }, [canBypassStoryMapPermissions, prototypePermission])

    const prototypeCanDelete = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (!prototypePermission) return false
        return Boolean(prototypePermission.isDelete)
    }, [canBypassStoryMapPermissions, prototypePermission])

    const swimLaneOptions = useMemo(() => {
        const lanes = projectDetail?.supportSwimlanes || []
        return lanes.map((data) => ({
            label: data?.name,
            value: data?._id,
            color: data?.color || 'cyan',
        }))
    }, [projectDetail?.supportSwimlanes])

    const isProjectAccessDenied = projectDetailStatus === 'access_denied'

    const currentProjectDetailRecord = useMemo(
        () => resolveProjectDetailRecord(projectDetail),
        [projectDetail]
    )

    // Only treat detail as ready when it matches the route project. Avoids
    // mounting the board (and firing assignee/status/list) on stale detail.
    const isCurrentProjectDetailReady = useMemo(() => {
        if (isProjectAccessDenied) return false
        if (projectDetailStatus !== 'loaded') return false
        if (!projectId) return false
        const detailId = currentProjectDetailRecord?._id
        if (!detailId) return false
        return String(detailId) === String(projectId)
    }, [
        isProjectAccessDenied,
        projectDetailStatus,
        projectId,
        currentProjectDetailRecord?._id,
    ])

    const getProjectDetail = async () => {
        if (!routeId) return

        let loadProjectId = routeId

        if (isWidgetMode) {
            const widgetCode = resolveWidgetCodeForRoute(
                routeId,
                location.search
            )
            if (!widgetCode) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }

            setWidgetAuthStatus('loading')
            let tokenPayload
            try {
                tokenPayload = await refreshWidgetTokenFromApi(widgetCode)
            } catch {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }
            if (!tokenPayload?.projectId) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }
            setWidgetResolvedProjectId(tokenPayload.projectId)
            loadProjectId = tokenPayload.projectId

            const widgetPayload = await getProjectByWidgetCode({ widgetCode })
            if (!widgetPayload?.success || !widgetPayload?.result?._id) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }

            const targetView = view || 'board'
            if (routeId !== loadProjectId) {
                // Stay on loading until the remounted route finishes fetching.
                navigate(
                    {
                        pathname: PRIVATE_ROUTES.projects.projectIdView(
                            loadProjectId,
                            targetView
                        ),
                        search: buildProjectWidgetSearch(widgetCode),
                    },
                    { replace: true }
                )
                return
            }
        }

        const result = await dispatch(fetchProjectDetail({ id: loadProjectId }))
        const data = result?.payload?.data

        if (isWidgetMode) {
            setWidgetAuthStatus('loaded')
        }

        if (
            data?.code === 'PROJECT_ACCESS_DENIED' ||
            (data?.success === false && data?.code === 'PROJECT_ACCESS_DENIED')
        ) {
            return
        }

        // Board/list APIs are loaded by the development/support tab effects once
        // this project's detail is ready — do not prefetch task/list here.
    }

    useEffect(() => {
        if (routeId) {
            getProjectDetail()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, routeId, isWidgetMode])

    useEffect(() => {
        if (!isWidgetMode) {
            clearWidgetSession()
            setWidgetAuthStatus('idle')
            setWidgetResolvedProjectId(null)
        }
    }, [isWidgetMode])

    useEffect(() => {
        developmentToolbarFilterRef.current = {}
        supportToolbarFilterRef.current = {}
        setSupportListQuery({
            isCompleted: null,
            mineAssignee: null,
        })
        setTaskListSearchTerm('')
    }, [projectId])

    useEffect(() => {
        if (!routeId || isProjectAccessDenied) return
        if (isWidgetMode && !projectId) return

        if (!view) {
            navigate(
                {
                    pathname: buildProjectViewPath(routeId, 'board', taskId),
                    search: widgetSearch,
                },
                { replace: true }
            )
            return
        }

        const taskTab = readTaskTabQuery(location.search)
        const isDevelopmentRoute =
            !isWidgetMode &&
            taskTab === PROJECT_TASK_TAB_DEVELOPMENT &&
            canAccessDevelopmentTab
        const topLevelViewRoutes = TOP_LEVEL_VIEW_ROUTES.filter((routeView) => {
            if (routeView === 'storyMap') return canAccessStoryMapRoute
            if (routeView === 'testrun') return canViewTestRun
            if (routeView === 'prototype') return canAccessPrototypeRoute
            return true
        })
        const validViews = isDevelopmentRoute
            ? DEVELOPMENT_VIEW_ROUTES
            : [...SUPPORT_VIEW_ROUTES, ...topLevelViewRoutes]

        const routeSearch = resolveProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            location.search
        )

        if (!validViews.includes(view)) {
            navigate(
                {
                    pathname: buildProjectViewPath(routeId, 'board', taskId),
                    search: routeSearch,
                },
                { replace: true }
            )
            return
        }

        if (view === 'storyMap' && !canAccessStoryMapRoute) {
            navigate(
                {
                    pathname: buildProjectViewPath(routeId, 'board', taskId),
                    search: routeSearch,
                },
                { replace: true }
            )
            return
        }

        if (view === 'prototype' && !canAccessPrototypeRoute) {
            navigate(
                {
                    pathname: buildProjectViewPath(routeId, 'board', taskId),
                    search: routeSearch,
                },
                { replace: true }
            )
            return
        }

        if (view === 'testrun' && !canViewTestRun) {
            navigate(
                {
                    pathname: buildProjectViewPath(routeId, 'board', taskId),
                    search: widgetSearch,
                },
                { replace: true }
            )
            return
        }

        if (
            view === 'files' ||
            view === 'reports' ||
            view === 'storyMap' ||
            view === 'prototype' ||
            view === 'testrun'
        ) {
            setActiveMainTab((prev) => (prev === view ? prev : view))
        } else if (
            !isWidgetMode &&
            taskTab === PROJECT_TASK_TAB_DEVELOPMENT &&
            canAccessDevelopmentTab
        ) {
            setActiveMainTab('development')
            const devView =
                view === 'list' || view === 'roadmap' || view === 'sprint'
                    ? view
                    : 'board'
            setDevelopmentSubView((prev) => (prev === devView ? prev : devView))
        } else if (canViewSupportTab) {
            setActiveMainTab((prev) => (prev === 'support' ? prev : 'support'))
            setSupportSubView((prev) => (prev === view ? prev : view))
        } else if (canAccessDevelopmentTab && !isWidgetMode) {
            setActiveMainTab('development')
        } else if (canViewStoryMap) {
            setActiveMainTab('storyMap')
        } else if (canViewPrototype) {
            setActiveMainTab('prototype')
        } else {
            setActiveMainTab('files')
        }
    }, [
        routeId,
        projectId,
        view,
        taskId,
        navigate,
        location.search,
        widgetSearch,
        isWidgetMode,
        canAccessDevelopmentTab,
        canAccessStoryMapRoute,
        canAccessPrototypeRoute,
        canViewTestRun,
        canViewSupportTab,
        canViewStoryMap,
        canViewPrototype,
        isProjectAccessDenied,
    ])

    useEffect(() => {
        if (isProjectAccessDenied || canViewStoryMap || isStoryMapAccessPending)
            return
        if (activeMainTab !== 'storyMap') return

        setActiveMainTab('support')
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    'board'
                ),
                search: resolveProjectRouteSearch(
                    isWidgetMode,
                    widgetSearch,
                    location.search
                ),
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewStoryMap,
        isStoryMapAccessPending,
        isProjectAccessDenied,
        navigate,
        routeId,
        widgetSearch,
        isWidgetMode,
        location.search,
    ])

    useEffect(() => {
        if (isProjectAccessDenied || canViewTestRun) return
        if (activeMainTab !== 'testrun') return

        setActiveMainTab('support')
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    'board'
                ),
                search: widgetSearch,
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewTestRun,
        isProjectAccessDenied,
        navigate,
        routeId,
        widgetSearch,
    ])

    useEffect(() => {
        if (
            isProjectAccessDenied ||
            canViewPrototype ||
            isPrototypeAccessPending
        )
            return
        if (activeMainTab !== 'prototype') return

        setActiveMainTab('support')
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    'board'
                ),
                search: resolveProjectRouteSearch(
                    isWidgetMode,
                    widgetSearch,
                    location.search
                ),
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewPrototype,
        isPrototypeAccessPending,
        isProjectAccessDenied,
        navigate,
        routeId,
        widgetSearch,
        isWidgetMode,
        location.search,
    ])

    useEffect(() => {
        if (!projectId || activeMainTab !== 'testrun') return undefined
        const previous = localStorage.getItem('currentProject')
        localStorage.setItem(
            'currentProject',
            JSON.stringify({ id: projectId })
        )
        return () => {
            if (previous) localStorage.setItem('currentProject', previous)
            else localStorage.removeItem('currentProject')
        }
    }, [projectId, activeMainTab])
    useEffect(() => {
        if (isProjectAccessDenied) return
        if (view === 'testrun' && taskId) {
            setActiveMainTab('testrun')
            setTaskDetailVisible(false)
            setTaskDetailFullscreen(false)
            setFullscreenAnimState('idle')
            setSelectedTaskId(null)
            setScrollToSection(null)
            ignoreTaskDrawerOutsideClickRef.current = false
            return
        }
        if (view === 'prototype' && taskId) {
            setActiveMainTab('prototype')
            setTaskDetailVisible(false)
            setTaskDetailFullscreen(false)
            setFullscreenAnimState('idle')
            setSelectedTaskId(null)
            setScrollToSection(null)
            ignoreTaskDrawerOutsideClickRef.current = false
            return
        }
        if (taskId) {
            const taskTab = readTaskTabQuery(location.search)
            if (
                taskTab === PROJECT_TASK_TAB_DEVELOPMENT &&
                canAccessDevelopmentTab
            ) {
                setActiveMainTab('development')
            }
            setSelectedTaskId(taskId)
            setTaskDetailVisible(true)
            ignoreTaskDrawerOutsideClickRef.current = true
            const timer = setTimeout(() => {
                ignoreTaskDrawerOutsideClickRef.current = false
            }, 500)
            return () => clearTimeout(timer)
        }

        setTaskDetailVisible(false)
        setTaskDetailFullscreen(false)
        setFullscreenAnimState('idle')
        setSelectedTaskId(null)
        setScrollToSection(null)
        ignoreTaskDrawerOutsideClickRef.current = false
    }, [
        taskId,
        view,
        isProjectAccessDenied,
        location.search,
        canAccessDevelopmentTab,
    ])

    useEffect(() => {
        if (isProjectAccessDenied) return
        if (
            (isWidgetMode || !canAccessDevelopmentTab) &&
            activeMainTab === 'development'
        ) {
            setActiveMainTab('support')
        }
    }, [
        activeMainTab,
        canAccessDevelopmentTab,
        isProjectAccessDenied,
        isWidgetMode,
    ])

    // Support module guard: kick the user off the Support tab when isRead is false.
    useEffect(() => {
        if (isProjectAccessDenied || canViewSupportTab) return
        if (activeMainTab !== 'support') return

        const fallbackTab =
            canAccessDevelopmentTab && !isWidgetMode
                ? 'development'
                : canViewStoryMap
                  ? 'storyMap'
                  : canViewPrototype
                    ? 'prototype'
                    : 'files'
        setActiveMainTab(fallbackTab)
        const fallbackView =
            fallbackTab === 'development' ? developmentSubView : fallbackTab
        const pathname =
            taskId &&
            (fallbackTab === 'development' || fallbackTab === 'support')
                ? PRIVATE_ROUTES.projects.projectIdViewTask(
                      routeId,
                      fallbackView,
                      taskId
                  )
                : PRIVATE_ROUTES.projects.projectIdView(routeId, fallbackView)
        navigate(
            {
                pathname,
                search: mergeProjectRouteSearch(
                    isWidgetMode,
                    widgetSearch,
                    fallbackTab === 'development'
                        ? buildProjectTaskTabSearch('development')
                        : ''
                ),
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewSupportTab,
        canAccessDevelopmentTab,
        canViewStoryMap,
        canViewPrototype,
        developmentSubView,
        isProjectAccessDenied,
        isWidgetMode,
        navigate,
        routeId,
        taskId,
        widgetSearch,
    ])

    useEffect(() => {
        if (isMemberModalVisible) {
            // Fetch users for invite dropdown
            dispatch(resetClientModalUsers())
            dispatch(
                usersListForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                    projectId,
                })
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isMemberModalVisible, projectId])

    const getProjectColor = (projectName) => {
        const colors = [
            '#60a5fa',
            '#34d399',
            '#a78bfa',
            '#f472b6',
            '#fb923c',
            '#fbbf24',
            '#4ade80',
            '#22d3ee',
        ]

        if (!projectName) return colors[0]

        let hash = 0
        for (let i = 0; i < projectName.length; i++) {
            hash = projectName.charCodeAt(i) + ((hash << 5) - hash)
        }

        return colors[Math.abs(hash) % colors.length]
    }

    const getMemberColor = (memberName) => {
        const colors = [
            '#60a5fa',
            '#34d399',
            '#a78bfa',
            '#f472b6',
            '#fb923c',
            '#fbbf24',
            '#4ade80',
            '#22d3ee',
        ]

        if (!memberName) return colors[0]

        let hash = 0
        for (let i = 0; i < memberName.length; i++) {
            hash = memberName.charCodeAt(i) + ((hash << 5) - hash)
        }

        return colors[Math.abs(hash) % colors.length]
    }

    const getInitials = (fullName) => {
        if (!fullName) return ''
        const names = fullName.trim().split(/\s+/)
        if (names.length >= 2) {
            return (
                names[0].charAt(0).toUpperCase() +
                names[names.length - 1].charAt(0).toUpperCase()
            )
        } else if (names.length === 1) {
            return names[0].charAt(0).toUpperCase()
        }
        return ''
    }

    const resolveClientAvatarSrc = (profilePicture) => {
        if (!profilePicture) return undefined
        if (String(profilePicture).startsWith('file://')) return undefined
        return profilePicture
    }

    const getClientAvatarSrc = useCallback((client) => {
        const fromApi = resolveClientAvatarSrc(client?.profilePicture)
        if (fromApi) return fromApi
        return getRandomAvatar({
            gender: client?.gender,
            seed: client?._id || client?.email || client?.userName,
        })
    }, [])

    const projectClients = useMemo(
        () =>
            Array.isArray(projectDetail?.clients)
                ? projectDetail.clients.filter((c) => c?._id)
                : [],
        [projectDetail?.clients]
    )

    const PROJECT_CLIENTS_VISIBLE = 5
    const visibleProjectClients = useMemo(
        () => projectClients?.slice(0, PROJECT_CLIENTS_VISIBLE),
        [projectClients]
    )
    const hiddenProjectClientsCount = Math.max(
        0,
        projectClients?.length - PROJECT_CLIENTS_VISIBLE
    )

    const clientModalFallbackProjects = useMemo(
        () =>
            projectDetail?._id
                ? [
                      {
                          _id: projectDetail._id,
                          name: projectDetail.name || 'Current project',
                      },
                  ]
                : [],
        [projectDetail?._id, projectDetail?.name]
    )

    const canManageProjectClients = useMemo(() => {
        if (isWidgetMode) return false
        if (userProfile?.isSuperAdmin) return true
        if (userProfile?.isSupportUser || userProfile?.isExistingUser) {
            const clientsPermission = getClientsPermission(
                userProfile?.supportProjectPermission
            )
            return clientsPermission?.isEdit === true
        }
        return false
    }, [
        isWidgetMode,
        userProfile?.isSuperAdmin,
        userProfile?.isSupportUser,
        userProfile?.isExistingUser,
        userProfile?.supportProjectPermission,
    ])

    const handleEditProjectClient = useCallback((client) => {
        setClientModalMode('edit')
        setEditingClient(client || null)
        setIsClientModalOpen(true)
        setProjectClientsPopoverOpen(false)
    }, [])

    const handleAddProjectClient = useCallback(() => {
        setClientModalMode('create')
        setEditingClient(null)
        setIsClientModalOpen(true)
        setProjectClientsPopoverOpen(false)
    }, [])

    const handleClientModalClose = useCallback(() => {
        setIsClientModalOpen(false)
        setClientModalMode('create')
        setEditingClient(null)
    }, [])

    const handleClientModalSaved = useCallback(() => {
        setIsClientModalOpen(false)
        setClientModalMode('create')
        setEditingClient(null)
        if (projectId) {
            dispatch(fetchProjectDetail({ id: projectId }))
        }
    }, [dispatch, projectId])

    const projectClientsPopoverContent = useMemo(
        () => (
            <div className={styles.projectClientsPopoverPanel}>
                <div className={styles.projectClientsPopoverHeader}>
                    <span className={styles.projectClientsPopoverTitle}>
                        Project clients
                    </span>
                    <span className={styles.projectClientsPopoverBadge}>
                        {projectClients.length}
                    </span>
                </div>
                {projectClients.length > 0 ? (
                    <ul className={styles.projectClientsPopoverList}>
                        {projectClients.map((client) => {
                            const displayName =
                                client?.userName || 'Unnamed client'
                            const avatarSrc = getClientAvatarSrc(client)

                            return (
                                <li
                                    key={client._id}
                                    className={styles.projectClientsPopoverItem}
                                    {...(canManageProjectClients
                                        ? {
                                              role: 'button',
                                              tabIndex: 0,
                                              onClick: () =>
                                                  handleEditProjectClient(
                                                      client
                                                  ),
                                              onKeyDown: (event) => {
                                                  if (
                                                      event.key === 'Enter' ||
                                                      event.key === ' '
                                                  ) {
                                                      event.preventDefault()
                                                      handleEditProjectClient(
                                                          client
                                                      )
                                                  }
                                              },
                                          }
                                        : { style: { cursor: 'default' } })}
                                >
                                    <Avatar
                                        size={32}
                                        src={avatarSrc}
                                        className={
                                            styles.projectClientsPopoverAvatar
                                        }
                                        style={{
                                            backgroundColor:
                                                getMemberColor(displayName),
                                            fontSize: 12,
                                            fontWeight: 600,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {!avatarSrc
                                            ? getInitials(displayName)
                                            : null}
                                    </Avatar>
                                    <div
                                        className={
                                            styles.projectClientsPopoverMeta
                                        }
                                    >
                                        <span
                                            className={
                                                styles.projectClientsPopoverName
                                            }
                                        >
                                            {displayName}
                                        </span>
                                        {client?.email ? (
                                            <span
                                                className={
                                                    styles.projectClientsPopoverEmail
                                                }
                                            >
                                                {client.email}
                                            </span>
                                        ) : null}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <div className={styles.projectClientsPopoverEmpty}>
                        No clients on this project yet
                    </div>
                )}
                {canManageProjectClients ? (
                    <div className={styles.projectClientsPopoverFooter}>
                        <button
                            type="button"
                            className={styles.projectClientsPopoverAddBtn}
                            onClick={handleAddProjectClient}
                        >
                            <Icon
                                icon="mdi:plus"
                                width={16}
                                height={16}
                                aria-hidden
                            />
                            <span>Add client</span>
                        </button>
                    </div>
                ) : null}
            </div>
        ),
        [
            projectClients,
            getClientAvatarSrc,
            handleEditProjectClient,
            handleAddProjectClient,
            canManageProjectClients,
        ]
    )

    // Convert hex to rgba with opacity
    const hexToRgba = (hex, opacity) => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }

    const handleCloseMemberModal = () => {
        setIsMemberModalVisible(false)
        setInviteEmail([])
        setSelectedMap({})
        setUserSearchValue('')
        dispatch(resetClientModalUsers())
    }

    // Debounced search for users
    const handleUserSearch = React.useMemo(
        () =>
            debounce((value) => {
                dispatch(setClientModalUserSearch(value))
                dispatch(
                    usersListForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                        projectId,
                    })
                )
            }, 300),
        [dispatch, projectId]
    )

    // Load more users on scroll
    const handleUserScroll = (e) => {
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            clientModalUserIsNext &&
            getUserStatus !== 'loading'
        ) {
            const nextPage = clientModalUserPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                usersListForClientModal({
                    skip: skip,
                    filter: clientModalUserSearch
                        ? { search: clientModalUserSearch }
                        : {},
                    limit: 10,
                    search: clientModalUserSearch,
                    projectId,
                })
            )
        }
    }

    const handleInvite = async () => {
        if (!inviteEmail || inviteEmail?.length === 0) {
            message.warning('Please select a user')
            return
        }

        setIsInviting(true)
        const invitedUserNames = inviteEmail
            ?.map((id) => selectedMap[id]?.textLabel || id)
            .join(', ')

        try {
            // Call API with array of userIds
            const result = await assignUserToProject({
                userIds: inviteEmail,
                projectId,
            })

            if (result?.data?.success !== false) {
                showActionNotification({
                    message: 'Invitation sent successfully!',
                    subMessage: invitedUserNames,
                })
                setInviteEmail([])
                setSelectedMap({})
                // Refresh project detail to get updated members list
                dispatch(fetchProjectDetail({ id: projectId }))
            } else {
                showActionNotification({
                    message: 'Failed to send invitations. Please try again.',
                    subMessage: invitedUserNames,
                })
            }
        } catch (error) {
            showActionNotification({
                message: 'Failed to send invitations. Please try again.',
                subMessage: invitedUserNames,
            })
        } finally {
            setIsInviting(false)
        }
    }

    const handleLeaveProject = (memberId, userName) => {
        Modal.confirm({
            title: 'Remove Member',
            content:
                'Are you sure you want to remove this member from the project?',
            okText: 'Leave',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    // Call API with array of userIds
                    const result = await unAssignUserToProject({
                        userIds: [memberId],
                        projectId,
                    })

                    if (result?.data?.success !== false) {
                        showActionNotification({
                            message: 'Member removed successfully!',
                            subMessage: userName,
                        })
                        dispatch(fetchProjectDetail({ id: projectId }))
                    } else {
                        showActionNotification({
                            message:
                                'Failed to remove member. Please try again.',
                            subMessage: userName,
                        })
                    }
                } catch (error) {
                    showActionNotification({
                        message: 'Failed to remove member. Please try again.',
                        subMessage: userName,
                    })
                }
            },
        })
    }

    const handleCopyProjectLink = useCallback(async () => {
        const linkProjectId = projectDetail?._id || projectId
        if (!linkProjectId) {
            message.warning('Project link is not ready yet')
            return
        }
        const link = `${window.location.origin}${PRIVATE_ROUTES.projects.projectId(linkProjectId)}`
        try {
            const ok = await copyTextToClipboard(link)
            if (!ok) throw new Error('copy failed')
            showActionNotification({
                message: 'Project link copied',
                // subMessage: link,
                notificationKey: 'project-link-copied',
            })
        } catch {
            showActionNotification({
                message: 'Failed to copy project link',
                subMessage: link,
                notificationKey: 'project-link-copy-failed',
            })
        }
    }, [projectId, projectDetail?._id])

    const handleTaskClick = useCallback(
        (clickedTaskId, scrollTo) => {
            if (String(clickedTaskId || '').startsWith('pending-create:')) {
                return
            }
            setSelectedTaskId(clickedTaskId)
            setTaskDetailVisible(true)
            setScrollToSection(scrollTo || null)

            const viewForRoute =
                activeMainTab === 'development'
                    ? developmentSubView
                    : supportSubView
            const search = mergeProjectRouteSearch(
                isWidgetMode,
                widgetSearch,
                activeMainTab === 'development'
                    ? buildProjectTaskTabSearch('development')
                    : ''
            )
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdViewTask(
                        routeId,
                        viewForRoute,
                        clickedTaskId
                    ),
                    search,
                },
                { replace: true }
            )
        },
        [
            activeMainTab,
            developmentSubView,
            supportSubView,
            isWidgetMode,
            widgetSearch,
            navigate,
            routeId,
        ]
    )

    const handleToggleFullscreen = useCallback(() => {
        if (taskDetailFullscreen) {
            // Play exit animation first; styles flip in onAnimationEnd
            setFullscreenAnimState('exiting')
        } else {
            setTaskDetailFullscreen(true)
            setFullscreenAnimState('entering')
        }
    }, [taskDetailFullscreen])

    const handleFullscreenAnimationEnd = useCallback(() => {
        if (fullscreenAnimState === 'exiting') {
            setTaskDetailFullscreen(false)
        }
        setFullscreenAnimState('idle')
    }, [fullscreenAnimState])

    useEffect(() => {
        taskDetailDrawerWidthRef.current = taskDetailDrawerWidth
    }, [taskDetailDrawerWidth])

    const applyTaskDetailDrawerWidth = useCallback(
        (width) => {
            taskDetailDrawerWidthRef.current = width
            const drawer = taskDetailDrawerRef.current
            const main = taskDetailMainContentRef.current

            if (drawer && !taskDetailFullscreen) {
                drawer.style.width = `${width}px`
            }
            if (main && taskDetailVisible && !taskDetailFullscreen) {
                main.style.width = `calc(100% - ${width}px)`
            }
        },
        [taskDetailFullscreen, taskDetailVisible]
    )

    const handleTaskDetailResizeStart = useCallback(
        (event) => {
            if (taskDetailFullscreen) return
            event.preventDefault()
            event.stopPropagation()

            const drawer = taskDetailDrawerRef.current
            const main = taskDetailMainContentRef.current

            taskDetailResizeRef.current = {
                startX: event.clientX,
                startWidth: taskDetailDrawerWidthRef.current,
            }
            taskDetailPendingWidthRef.current = null

            drawer?.classList.add(styles.taskDetailDrawerResizing)
            main?.classList.add(styles.taskDetailMainResizing)
            document.body.classList.add('taskDetailResizeActive')

            const scheduleWidthApply = (width) => {
                taskDetailPendingWidthRef.current = width
                if (taskDetailResizeRafRef.current != null) return
                taskDetailResizeRafRef.current = requestAnimationFrame(() => {
                    taskDetailResizeRafRef.current = null
                    const nextWidth = taskDetailPendingWidthRef.current
                    if (nextWidth == null) return
                    applyTaskDetailDrawerWidth(nextWidth)
                })
            }

            const handleMouseMove = (moveEvent) => {
                const { startX, startWidth } = taskDetailResizeRef.current
                const nextWidth = Math.min(
                    TASK_DETAIL_DRAWER_MAX_WIDTH,
                    Math.max(
                        TASK_DETAIL_DRAWER_MIN_WIDTH,
                        startWidth + (startX - moveEvent.clientX)
                    )
                )
                scheduleWidthApply(nextWidth)
            }

            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)

                if (taskDetailResizeRafRef.current != null) {
                    cancelAnimationFrame(taskDetailResizeRafRef.current)
                    taskDetailResizeRafRef.current = null
                }

                const finalWidth =
                    taskDetailPendingWidthRef.current ??
                    taskDetailDrawerWidthRef.current
                applyTaskDetailDrawerWidth(finalWidth)
                setTaskDetailDrawerWidth(finalWidth)
                taskDetailPendingWidthRef.current = null

                drawer?.classList.remove(styles.taskDetailDrawerResizing)
                main?.classList.remove(styles.taskDetailMainResizing)
                document.body.classList.remove('taskDetailResizeActive')
                document.body.style.cursor = ''
                document.body.style.userSelect = ''
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
        },
        [applyTaskDetailDrawerWidth, taskDetailFullscreen]
    )

    const handleScrollToSectionHandled = useCallback(() => {
        setScrollToSection(null)
    }, [])

    const handleTaskDetailClose = useCallback(() => {
        setTaskDetailVisible(false)
        setTaskDetailFullscreen(false)
        setFullscreenAnimState('idle')
        setSelectedTaskId(null)
        setScrollToSection(null)
        const viewForRoute =
            activeMainTab === 'development'
                ? developmentSubView
                : supportSubView
        const search = mergeProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            activeMainTab === 'development'
                ? buildProjectTaskTabSearch('development')
                : ''
        )
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    viewForRoute
                ),
                search,
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        developmentSubView,
        routeId,
        isWidgetMode,
        widgetSearch,
        navigate,
        supportSubView,
    ])

    const handleMainTabChange = useCallback(
        (nextTab) => {
            if (nextTab === 'development' && !canAccessDevelopmentTab) return
            if (nextTab === 'storyMap' && !canViewStoryMap) return
            if (nextTab === 'prototype' && !canViewPrototype) return
            if (nextTab === 'testrun' && !canViewTestRun) return
            if (nextTab === 'support' && !canViewSupportTab) return

            setActiveMainTab(nextTab)
            setTaskDetailVisible(false)
            setTaskDetailFullscreen(false)
            setFullscreenAnimState('idle')
            setSelectedTaskId(null)
            setScrollToSection(null)

            const targetView =
                nextTab === 'development'
                    ? developmentSubView
                    : nextTab === 'files' ||
                        nextTab === 'reports' ||
                        nextTab === 'storyMap' ||
                        nextTab === 'prototype' ||
                        nextTab === 'testrun'
                      ? nextTab
                      : supportSubView
            const search = mergeProjectRouteSearch(
                isWidgetMode,
                widgetSearch,
                nextTab === 'development'
                    ? buildProjectTaskTabSearch('development')
                    : ''
            )

            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        routeId,
                        targetView
                    ),
                    search,
                },
                { replace: true }
            )
        },
        [
            canAccessDevelopmentTab,
            canViewStoryMap,
            canViewPrototype,
            canViewTestRun,
            canViewSupportTab,
            developmentSubView,
            supportSubView,
            isWidgetMode,
            widgetSearch,
            navigate,
            routeId,
        ]
    )

    const handleDevelopmentSubViewChange = useCallback(
        (nextView) => {
            if (!DEVELOPMENT_VIEW_ROUTES.includes(nextView)) return
            setDevelopmentSubView(nextView)
            const search = mergeProjectRouteSearch(
                isWidgetMode,
                widgetSearch,
                buildProjectTaskTabSearch('development')
            )
            const pathname = taskId
                ? PRIVATE_ROUTES.projects.projectIdViewTask(
                      routeId,
                      nextView,
                      taskId
                  )
                : PRIVATE_ROUTES.projects.projectIdView(routeId, nextView)
            navigate({ pathname, search }, { replace: true })
        },
        [isWidgetMode, widgetSearch, taskId, routeId, navigate]
    )

    const handleSupportSubViewChange = useCallback(
        (nextView) => {
            if (!SUPPORT_VIEW_ROUTES.includes(nextView)) return
            setSupportSubView(nextView)
            const pathname = taskId
                ? PRIVATE_ROUTES.projects.projectIdViewTask(
                      routeId,
                      nextView,
                      taskId
                  )
                : PRIVATE_ROUTES.projects.projectIdView(routeId, nextView)
            navigate({ pathname, search: widgetSearch }, { replace: true })
        },
        [taskId, routeId, navigate, widgetSearch]
    )

    const applyDevelopmentToolbarQuery = useCallback(
        (toolbarFilter = {}, { skip = 0, silent = false } = {}) => {
            if (!projectId) return

            developmentToolbarFilterRef.current = mergeToolbarFilter(
                developmentToolbarFilterRef.current,
                toolbarFilter
            )
            const mergedFilter = developmentToolbarFilterRef.current

            if (developmentSubView === 'sprint') {
                const boardQuery = buildDevelopmentSprintListQuery(mergedFilter)
                const { search: _search, ...epicQuery } = boardQuery
                trackToolbarQuery(
                    dispatch(
                        loadDevelopmentSprintBoard({
                            projectId,
                            epicQuery,
                            sprintQuery: boardQuery,
                        })
                    ),
                    silent
                )
                return
            }

            trackToolbarQuery(
                dispatch(
                    mainTaskList(
                        buildMainTaskListRequest({
                            projectId,
                            toolbarFilter: mergedFilter,
                            search: mergedFilter.search,
                            skip,
                        })
                    )
                ),
                silent
            )
        },
        [dispatch, projectId, developmentSubView, trackToolbarQuery]
    )

    const applySupportToolbarQuery = useCallback(
        (toolbarFilter = {}, { skip = 0, silent = false } = {}) => {
            if (!projectId) return

            supportToolbarFilterRef.current = mergeToolbarFilter(
                supportToolbarFilterRef.current,
                toolbarFilter
            )
            const filter = supportToolbarFilterRef.current
            const hasUnassigned =
                Array.isArray(filter.selectedAssignees) &&
                filter.selectedAssignees.includes('UNASSIGNED')
            const searchTerm = String(filter.search ?? '').trim()

            setSupportListQuery({
                isCompleted:
                    typeof filter.isCompleted === 'boolean'
                        ? filter.isCompleted
                        : null,
                mineAssignee: filter.mineAssignee || null,
            })

            trackToolbarQuery(
                dispatch(
                taskListSupport({
                    skip,
                    limit: 10,
                    filter: {
                        projectId,
                        ...(hasUnassigned
                            ? { isUnassigned: true }
                            : {
                                  assignee:
                                      filter.mineAssignee ||
                                      filter.selectedAssignees,
                              }),
                        status: filter.selectedStatus,
                        ...(filter.selectedType?.length === 1
                            ? { type: filter.selectedType[0] }
                            : filter.selectedType?.length > 1
                              ? { type: filter.selectedType }
                              : {}),
                        ...(typeof filter.isCompleted === 'boolean'
                            ? { isCompleted: filter.isCompleted }
                            : {}),
                    },
                    listType: 'status',
                    ...(searchTerm ? { search: searchTerm } : {}),
                })
                ),
                silent
            )
        },
        [dispatch, projectId, trackToolbarQuery]
    )

    // Refetch board/list (and sprint) whenever those tabs become active so
    // data stays fresh after creating/updating tasks in another view.
    useEffect(() => {
        if (
            !isCurrentProjectDetailReady ||
            !projectId ||
            isProjectAccessDenied ||
            activeMainTab !== 'development'
        ) {
            return
        }
        applyDevelopmentToolbarQuery()
    }, [
        isCurrentProjectDetailReady,
        projectId,
        activeMainTab,
        developmentSubView,
        isProjectAccessDenied,
        applyDevelopmentToolbarQuery,
    ])

    useEffect(() => {
        if (
            !isCurrentProjectDetailReady ||
            !projectId ||
            isProjectAccessDenied ||
            activeMainTab !== 'support'
        ) {
            return
        }
        applySupportToolbarQuery()
    }, [
        isCurrentProjectDetailReady,
        projectId,
        activeMainTab,
        supportSubView,
        isProjectAccessDenied,
        applySupportToolbarQuery,
    ])

    useEffect(() => {
        selectedTaskIdRef.current = selectedTaskId
    }, [selectedTaskId])

    const handleTaskUpdate = useCallback(() => {
        const currentTaskId = selectedTaskIdRef.current
        if (currentTaskId) {
            if (activeMainTab === 'development') {
                dispatch(mainTaskById({ _id: currentTaskId }))
            } else {
                dispatch(taskById({ _id: currentTaskId }))
            }
        }
        // Background sync after a task edit — keep the current view on screen.
        if (activeMainTab === 'development') {
            applyDevelopmentToolbarQuery({}, { silent: true })
        } else {
            applySupportToolbarQuery({}, { silent: true })
        }
    }, [
        activeMainTab,
        dispatch,
        applyDevelopmentToolbarQuery,
        applySupportToolbarQuery,
    ])

    const handleTaskTitleLocalUpdate = useCallback((taskId, nextTitle) => {
        if (!taskId) return
        setLiveTaskTitlePatch({
            taskId,
            title: nextTitle,
            updatedAt: Date.now(),
        })
    }, [])

    const handleArchiveToggle = async () => {
        const archiveProjectId = projectDetail?._id || projectId
        if (!archiveProjectId) return

        const nextArchivedValue = !Boolean(projectDetail?.isArchived)
        const result = await dispatch(
            updateProject({
                _id: archiveProjectId,
                updatedData: {
                    isArchived: nextArchivedValue,
                },
            })
        )

        if (result?.payload?.data?.success) {
            showActionNotification({
                message: nextArchivedValue
                    ? 'Project archived successfully'
                    : 'Project unarchived successfully',
                subMessage: projectDetail?.name,
                notificationKey: nextArchivedValue
                    ? 'project-archived'
                    : 'project-unarchived',
            })
            dispatch(fetchProjectDetail({ id: archiveProjectId }))
        } else {
            showActionNotification({
                type: 'error',
                message: nextArchivedValue
                    ? 'Could not archive project'
                    : 'Could not unarchive project',
                subMessage:
                    result?.payload?.data?.message || 'Please try again.',
                notificationKey: 'project-archive-error',
            })
        }

        setProjectMenuOpen(false)
    }

    // Handle click outside task detail drawer to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!taskDetailVisible || !taskDetailDrawerRef.current) return
            if (ignoreTaskDrawerOutsideClickRef.current) return

            // In fullscreen mode the drawer is position:fixed covering the entire
            // viewport — there is no "outside", so the close-on-outside-click
            // logic must be disabled. (This also prevents stale-closure issues
            // where contains() could return false for clicks on topBarActions.)
            if (taskDetailFullscreen) return

            // composedPath() pierces shadow-DOM boundaries (e.g. em-emoji-picker
            // web component), so we check both the retargeted event.target and
            // every element in the full composed path.
            const composedPath = event.composedPath ? event.composedPath() : []
            const isInsideDrawer =
                taskDetailDrawerRef.current.contains(event.target) ||
                composedPath.includes(taskDetailDrawerRef.current)

            if (!isInsideDrawer) {
                // Check if click is not on a modal or popover
                const isModal = event.target.closest?.('.ant-modal')
                const isPopover = event.target.closest?.('.ant-popover')
                const isDropdown = event.target.closest?.('.ant-dropdown')

                if (!isModal && !isPopover && !isDropdown) {
                    handleTaskDetailClose()
                }
            }
        }

        if (taskDetailVisible) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [taskDetailVisible, taskDetailFullscreen, handleTaskDetailClose])

    // Format user options from slice data
    const userOptions = React.useMemo(() => {
        if (clientModalUsersListData && clientModalUsersListData?.length > 0) {
            return clientModalUsersListData.map((user) => ({
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {user?.profilePicture ? (
                            <Avatar size={28} src={user?.profilePicture} />
                        ) : (
                            <Avatar
                                size={28}
                                style={{
                                    backgroundColor: getMemberColor(user?.name),
                                }}
                            >
                                {getInitials(user?.name)}
                            </Avatar>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                                {user?.name}
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                                {user?.email}
                            </span>
                        </div>
                    </div>
                ),
                value: user?._id,
                user: user,
                textLabel: user?.name || user?.email || 'Unnamed User',
                profilePicture: user?.profilePicture,
            }))
        }
        return []
    }, [clientModalUsersListData])

    const projectIconContrastColor = useMemo(() => {
        const pc =
            projectDetail?.colorCode || getProjectColor(projectDetail?.name)
        return harmonicIconToneForBackground(pc)
    }, [projectDetail])

    if (isWidgetMode && widgetAuthStatus === 'error') {
        return (
            <div className={styles.emptyState}>
                <Icon
                    icon="mdi:link-variant-off"
                    className={styles.emptyIcon}
                />
                <Text className={styles.emptyText}>Widget not found</Text>
                <Text className={styles.emptySubText}>
                    This embed link is invalid or no longer available.
                </Text>
            </div>
        )
    }

    if (
        isWidgetMode &&
        (widgetAuthStatus === 'idle' || widgetAuthStatus === 'loading')
    ) {
        return (
            <div className={styles.loadingContainer}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (
        !isProjectAccessDenied &&
        projectDetailStatus !== 'error' &&
        (projectDetailStatus === 'loading' ||
            projectDetailStatus === 'idle' ||
            !isCurrentProjectDetailReady)
    ) {
        return (
            <div className={styles.loadingContainer}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (isProjectAccessDenied) {
        return (
            <ProjectAccessDeniedView
                message={projectAccessDeniedMessage}
                activeProjectId={userProfile?.activeProjectId?._id}
                currentProjectId={routeId}
            />
        )
    }

    if (!projectDetail) {
        if (isWidgetMode) {
            return (
                <div className={styles.emptyState}>
                    <Icon
                        icon="mdi:link-variant-off"
                        className={styles.emptyIcon}
                    />
                    <Text className={styles.emptyText}>Widget not found</Text>
                    <Text className={styles.emptySubText}>
                        This embed link is invalid or no longer available.
                    </Text>
                </div>
            )
        }
        return (
            <div className={styles.emptyState}>
                <Icon
                    icon="mdi:folder-off-outline"
                    className={styles.emptyIcon}
                />
                <Text className={styles.emptyText}>Project not found</Text>
                <Button
                    type="primary"
                    onClick={() => navigate(-1)}
                    className={styles.backButton}
                >
                    Go Back
                </Button>
            </div>
        )
    }

    const projectColor =
        projectDetail?.colorCode || getProjectColor(projectDetail?.name)

    return (
        <ProjectPermissionsContext.Provider
            value={projectPermissionsContextValue}
        >
            <div
                className={styles.projectDetailContainer}
                id="project-detail-scroll"
            >
                <div
                    className={styles.projectChrome}
                    id="project-chrome"
                    data-project-chrome="true"
                >
                    <div className={styles.projectInfo}>
                        <div className={styles.projectAvatarSection}>
                            <Popover
                                trigger={['click']}
                                open={avatarColorIconPopoverOpen}
                                onOpenChange={(open) => {
                                    setAvatarColorIconPopoverOpen(open)
                                    if (open) {
                                        setProjectMenuOpen(false)
                                        setColorIconPopoverOpen(false)
                                    }
                                }}
                                placement="bottomLeft"
                                classNames={{
                                    root: styles.colorIconPopoverOverlay,
                                }}
                                getPopupContainer={() => document.body}
                                content={
                                    <ProjectColorIconFlyout
                                        projectId={
                                            projectDetail?._id || projectId
                                        }
                                        palette={DEFAULT_COLOR_PALETTE}
                                        initialColor={projectDetail?.colorCode}
                                        onSaved={() =>
                                            setAvatarColorIconPopoverOpen(false)
                                        }
                                    />
                                }
                                arrow={false}
                            >
                                <div
                                    className={styles.projectDetailIcon}
                                    style={{
                                        backgroundColor: projectColor,
                                        cursor: 'pointer',
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Set project color & icon"
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault()
                                            setAvatarColorIconPopoverOpen(true)
                                        }
                                    }}
                                >
                                    {projectDetail?.urlType === 'icon' &&
                                    projectDetail?.url ? (
                                        <Icon
                                            icon={projectDetail.url}
                                            width={18}
                                            height={18}
                                            style={{
                                                color: projectIconContrastColor,
                                            }}
                                        />
                                    ) : projectDetail?.urlType === 'upload' &&
                                      projectDetail?.url ? (
                                        <img
                                            src={projectDetail.url}
                                            alt={projectDetail.name || ''}
                                            className={
                                                styles.projectDetailIconImage
                                            }
                                        />
                                    ) : !projectDetail?.colorCode &&
                                      projectDetail?.projectProfile ? (
                                        <img
                                            src={projectDetail.projectProfile}
                                            alt={projectDetail.name || ''}
                                            className={
                                                styles.projectDetailIconImage
                                            }
                                        />
                                    ) : !projectDetail?.colorCode ? (
                                        <Text
                                            className={
                                                styles.projectDetailIconText
                                            }
                                        >
                                            {projectDetail?.name
                                                ?.charAt(0)
                                                ?.toUpperCase()}
                                        </Text>
                                    ) : null}
                                </div>
                            </Popover>
                        </div>
                        <div className={styles.projectDetails}>
                            <div className={styles.projectTitleRow}>
                                <Title level={4} className={styles.projectName}>
                                    {projectDetail?.name || 'Unnamed Project'}
                                </Title>
                                {userProfile &&
                                    (userProfile?.isSuperAdmin ||
                                        userProfile?.isExistingUser) && (
                                        <Dropdown
                                            trigger={['click']}
                                            open={projectMenuOpen}
                                            onOpenChange={(open) => {
                                                setProjectMenuOpen(open)
                                                if (!open)
                                                    setColorIconPopoverOpen(
                                                        false
                                                    )
                                            }}
                                            placement="bottomLeft"
                                            overlayClassName={
                                                styles.projectMenuDropdown
                                            }
                                            popupRender={() => (
                                                <div
                                                    className={
                                                        styles.customProjectMenu
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.customProjectMenuItem
                                                        }
                                                        onClick={() => {
                                                            setEditProjectOpen(
                                                                true
                                                            )
                                                            setProjectMenuOpen(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        <Icon
                                                            icon="mdi:pencil"
                                                            width={16}
                                                            height={16}
                                                            style={{
                                                                color: '#757677',
                                                                marginRight: 8,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span>
                                                            Edit Project
                                                        </span>
                                                    </button>
                                                    <Popover
                                                        trigger={['hover']}
                                                        open={
                                                            colorIconPopoverOpen
                                                        }
                                                        onOpenChange={
                                                            setColorIconPopoverOpen
                                                        }
                                                        placement="rightTop"
                                                        overlayClassName={
                                                            styles.colorIconPopoverOverlay
                                                        }
                                                        getPopupContainer={(
                                                            triggerNode
                                                        ) =>
                                                            triggerNode?.closest?.(
                                                                '.ant-dropdown'
                                                            ) || document.body
                                                        }
                                                        content={
                                                            <ProjectColorIconFlyout
                                                                projectId={
                                                                    projectDetail?._id ||
                                                                    projectId
                                                                }
                                                                palette={
                                                                    DEFAULT_COLOR_PALETTE
                                                                }
                                                                initialColor={
                                                                    projectDetail?.colorCode
                                                                }
                                                                onSaved={() =>
                                                                    setProjectMenuOpen(
                                                                        false
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        arrow={false}
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`${styles.customProjectMenuItem} ${styles.customProjectMenuItemRow}`}
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.menuColorDot
                                                                }
                                                                style={{
                                                                    backgroundColor:
                                                                        projectDetail?.colorCode ||
                                                                        projectColor,
                                                                    width: 16,
                                                                    height: 16,
                                                                }}
                                                                aria-hidden
                                                            />
                                                            <span
                                                                style={{
                                                                    flex: 1,
                                                                    textAlign:
                                                                        'left',
                                                                }}
                                                            >
                                                                Set color & icon
                                                            </span>
                                                            <Icon
                                                                icon="mdi:chevron-right"
                                                                width={18}
                                                                height={18}
                                                                style={{
                                                                    color: '#94a3b8',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        </button>
                                                    </Popover>
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.customProjectMenuItem
                                                        }
                                                        onClick={
                                                            handleCopyProjectLink
                                                        }
                                                    >
                                                        <Icon
                                                            icon="qlementine-icons:link-16"
                                                            width={16}
                                                            height={16}
                                                            style={{
                                                                color: '#757677',
                                                                marginRight: 8,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span>
                                                            Copy project link
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.customProjectMenuItem
                                                        }
                                                        onClick={() => {
                                                            setImportModalVisible(
                                                                true
                                                            )
                                                            setProjectMenuOpen(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        <Icon
                                                            icon="hugeicons:file-import"
                                                            width={16}
                                                            height={16}
                                                            style={{
                                                                color: '#757677',
                                                                marginRight: 8,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span>Import</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.customProjectMenuItem
                                                        }
                                                        onClick={
                                                            handleArchiveToggle
                                                        }
                                                    >
                                                        <Icon
                                                            icon={
                                                                'fluent:archive-20-regular'
                                                            }
                                                            width={16}
                                                            height={16}
                                                            style={{
                                                                color: '#757677',
                                                                marginRight: 8,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span>
                                                            {projectDetail?.isArchived
                                                                ? 'Unarchive Project'
                                                                : 'Archive Project'}
                                                        </span>
                                                    </button>
                                                </div>
                                            )}
                                        >
                                            <Icon
                                                icon="mdi:chevron-down"
                                                width={20}
                                                height={20}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#64748b',
                                                    transition:
                                                        'transform 0.2s',
                                                    transform: projectMenuOpen
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setProjectMenuOpen(
                                                        !projectMenuOpen
                                                    )
                                                }}
                                            />
                                        </Dropdown>
                                    )}
                                {projectClients?.length > 0 ||
                                canManageProjectClients ? (
                                    <>
                                        <span
                                            className={
                                                styles.projectTitleDivider
                                            }
                                            aria-hidden
                                        />
                                        {projectClients.length > 0 ? (
                                            <Popover
                                                trigger={['click']}
                                                placement="bottomLeft"
                                                arrow={false}
                                                open={
                                                    projectClientsPopoverOpen
                                                }
                                                onOpenChange={
                                                    setProjectClientsPopoverOpen
                                                }
                                                overlayClassName={
                                                    styles.projectClientsPopover
                                                }
                                                content={
                                                    projectClientsPopoverContent
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.projectClientsPill
                                                    }
                                                    aria-label={`${projectClients.length} project clients. Click to view all.`}
                                                >
                                                    <span
                                                        className={
                                                            styles.projectClientsCompactLabel
                                                        }
                                                    >
                                                        Clients
                                                    </span>
                                                    <span
                                                        className={
                                                            styles.projectClientsCountBadge
                                                        }
                                                    >
                                                        {
                                                            projectClients.length
                                                        }
                                                    </span>
                                                    <div
                                                        className={
                                                            styles.projectClientsAvatars
                                                        }
                                                    >
                                                        {visibleProjectClients.map(
                                                            (client) => {
                                                                const displayName =
                                                                    client?.userName ||
                                                                    'Unnamed client'
                                                                const avatarSrc =
                                                                    getClientAvatarSrc(
                                                                        client
                                                                    )
                                                                const tip =
                                                                    displayName

                                                                return (
                                                                    <Tooltip
                                                                        key={
                                                                            client._id
                                                                        }
                                                                        title={
                                                                            tip
                                                                        }
                                                                        mouseEnterDelay={
                                                                            0.35
                                                                        }
                                                                    >
                                                                        <Avatar
                                                                            size={
                                                                                26
                                                                            }
                                                                            src={
                                                                                avatarSrc
                                                                            }
                                                                            className={
                                                                                styles.projectClientAvatarChip
                                                                            }
                                                                            style={{
                                                                                backgroundColor:
                                                                                    getMemberColor(
                                                                                        displayName
                                                                                    ),
                                                                                fontSize: 10,
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {!avatarSrc
                                                                                ? getInitials(
                                                                                      displayName
                                                                                  )
                                                                                : null}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                )
                                                            }
                                                        )}
                                                        {hiddenProjectClientsCount >
                                                        0 ? (
                                                            <span
                                                                className={
                                                                    styles.projectClientsMoreChip
                                                                }
                                                                aria-hidden
                                                            >
                                                                +
                                                                {
                                                                    hiddenProjectClientsCount
                                                                }
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <Icon
                                                        icon="mdi:chevron-down"
                                                        className={
                                                            styles.projectClientsChevron
                                                        }
                                                        width={16}
                                                        height={16}
                                                        aria-hidden
                                                    />
                                                </button>
                                            </Popover>
                                        ) : (
                                            <button
                                                type="button"
                                                className={`${styles.projectClientsPill} ${styles.projectClientsPillEmpty}${
                                                    canManageProjectClients
                                                        ? ` ${styles.projectClientsPillEmptyAction}`
                                                        : ''
                                                }`}
                                                onClick={
                                                    canManageProjectClients
                                                        ? handleAddProjectClient
                                                        : undefined
                                                }
                                                aria-label={
                                                    canManageProjectClients
                                                        ? 'No project clients. Click to add a client.'
                                                        : 'No project clients'
                                                }
                                            >
                                                <span
                                                    className={
                                                        styles.projectClientsCompactLabel
                                                    }
                                                >
                                                    No clients
                                                </span>
                                                {canManageProjectClients ? (
                                                    <Icon
                                                        icon="mdi:plus"
                                                        width={14}
                                                        height={14}
                                                        className={
                                                            styles.projectClientsEmptyAddIcon
                                                        }
                                                        aria-hidden
                                                    />
                                                ) : null}
                                            </button>
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </div>
                        {/* <div className={styles.membersAvatars}>
                    {projectDetail?.members &&
                        projectDetail.members.length > 0 && (
                            <Avatar.Group
                                max={{
                                    count: 5,
                                    popover: {
                                        trigger: [],
                                    },
                                    style: {
                                        backgroundColor: '#6366f1',
                                        cursor: 'pointer',
                                        color: '#ffffff',
                                    },
                                }}
                                size={32}
                            >
                                {projectDetail.members.map((member) => {
                                    const memberColor = getMemberColor(
                                        member?.userName
                                    )
                                    const initials = getInitials(
                                        member?.userName
                                    )

                                    return (
                                        <Avatar
                                            key={member?._id}
                                            size={32}
                                            className={styles.memberAvatarGroup}
                                            src={member?.profilePicture}
                                            style={{
                                                backgroundColor: memberColor,
                                                fontSize: 12,
                                            }}
                                            onClick={() =>
                                                handleMemberClick(member)
                                            }
                                        >
                                            {initials || (
                                                <Icon
                                                    icon="mdi:account"
                                                    style={{ fontSize: 12 }}
                                                />
                                            )}
                                        </Avatar>
                                    )
                                })}
                            </Avatar.Group>
                        )}
                    <Tooltip title="Assign member">
                        <button
                            className={styles.moreOptionsButton}
                            onClick={() => handleMemberClick()}
                        >
                            <Icon
                                icon="mdi:dots-horizontal"
                                style={{ fontSize: 16 }}
                            />
                        </button>
                    </Tooltip>
                </div> */}
                    </div>

                    <ProjectMainTabs
                        activeMainTab={activeMainTab}
                        onTabChange={handleMainTabChange}
                        projectId={projectId}
                        canViewStoryMap={canViewStoryMap}
                        canViewPrototype={canViewPrototype}
                        canViewSupportTab={canViewSupportTab}
                        canAccessDevelopmentTab={canAccessDevelopmentTab}
                        canViewTestRun={canViewTestRun}
                        canCopyWidgetLink={canCopyWidgetLink}
                        canAccessStoryMapRoute={canAccessStoryMapRoute}
                        canAccessPrototypeRoute={canAccessPrototypeRoute}
                        developmentSubView={developmentSubView}
                        supportSubView={supportSubView}
                        addTaskToFirstSwimlaneRef={addTaskToFirstSwimlaneRef}
                        setTaskListSearchTerm={setTaskListSearchTerm}
                        applyDevelopmentToolbarQuery={applyDevelopmentToolbarQuery}
                        applySupportToolbarQuery={applySupportToolbarQuery}
                    />
                </div>
                <div
                    ref={taskDetailMainContentRef}
                    id="project-main-content"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflowY: 'hidden',
                        overflowX: 'hidden',
                        scrollbarColor: '#a0a1a1 #fffbfb',
                        flex: '1 1 0',
                        position: 'relative',
                        width:
                            taskDetailVisible && !taskDetailFullscreen
                                ? `calc(100% - ${taskDetailDrawerWidth}px)`
                                : '100%',
                    }}
                >
                    <ProjectMainContent
                        activeMainTab={activeMainTab}
                        canViewSupportTab={canViewSupportTab}
                        supportSubView={supportSubView}
                        developmentSubView={developmentSubView}
                        projectId={projectId}
                        routeId={routeId}
                        taskId={taskId}
                        onTaskClick={handleTaskClick}
                        taskDetailVisible={taskDetailVisible}
                        addTaskToFirstSwimlaneRef={addTaskToFirstSwimlaneRef}
                        liveTaskTitlePatch={liveTaskTitlePatch}
                        applySupportToolbarQuery={applySupportToolbarQuery}
                        supportListQuery={supportListQuery}
                        taskListSearchTerm={taskListSearchTerm}
                        applyDevelopmentToolbarQuery={applyDevelopmentToolbarQuery}
                        showTaskListRefreshOverlay={showTaskListRefreshOverlay}
                        filteredAssignees={filteredAssignees}
                        projectName={projectDetail?.name}
                        canViewTestRun={canViewTestRun}
                        canAccessStoryMapRoute={canAccessStoryMapRoute}
                        storyMapReadOnly={storyMapReadOnly}
                        storyMapCanDelete={storyMapCanDelete}
                        canAccessPrototypeRoute={canAccessPrototypeRoute}
                        routeView={view}
                        prototypeReadOnly={prototypeReadOnly}
                        prototypeCanDelete={prototypeCanDelete}
                        developmentViewTabs={DEVELOPMENT_VIEW_TABS}
                        supportViewTabs={SUPPORT_VIEW_TABS}
                        onDevelopmentSubViewChange={handleDevelopmentSubViewChange}
                        onSupportSubViewChange={handleSupportSubViewChange}
                    />
                    {/* Drawer - Slides in from right */}
                </div>
                {activeMainTab === 'storyMap' ? (
                    <div
                        id="story-map-drawer-host"
                        className={styles.storyMapDrawerHost}
                    />
                ) : null}
                <ProjectTaskDrawer
                    activeMainTab={activeMainTab}
                    drawerRef={taskDetailDrawerRef}
                    fullscreenAnimState={fullscreenAnimState}
                    onFullscreenAnimationEnd={handleFullscreenAnimationEnd}
                    isFullscreen={taskDetailFullscreen}
                    width={taskDetailDrawerWidth}
                    visible={taskDetailVisible}
                    onResizeStart={handleTaskDetailResizeStart}
                    taskId={selectedTaskId}
                    onClose={handleTaskDetailClose}
                    projectId={projectId}
                    filteredAssignees={filteredAssignees}
                    onTaskUpdate={handleTaskUpdate}
                    onTaskTitleLocalUpdate={handleTaskTitleLocalUpdate}
                    onTaskClick={handleTaskClick}
                    scrollToSection={scrollToSection}
                    onScrollToSectionHandled={handleScrollToSectionHandled}
                    swimLaneOptions={swimLaneOptions}
                    onToggleFullscreen={handleToggleFullscreen}
                />
                {/* Members Modal */}
                <Modal
                    title={null}
                    open={isMemberModalVisible}
                    onCancel={handleCloseMemberModal}
                    footer={null}
                    className={styles.memberModal}
                    width={520}
                    closeIcon={
                        <Icon icon="mdi:close" style={{ fontSize: 20 }} />
                    }
                >
                    <div className={styles.memberModalContent}>
                        {/* Modal Header */}
                        <div className={styles.modalHeader}>
                            <Title level={4} className={styles.modalTitle}>
                                Share {projectDetail?.name || 'Project'}
                            </Title>
                        </div>

                        {/* Invite Section */}
                        <div className={styles.inviteSection}>
                            <Select
                                mode="multiple"
                                value={inviteEmail}
                                removeIcon={
                                    <div
                                        style={{
                                            height: '14px',
                                            width: '14px',
                                        }}
                                    >
                                        <CloseModalIcon />
                                    </div>
                                }
                                placeholder="Add people..."
                                size="small"
                                filterOption={false}
                                searchValue={userSearchValue}
                                onSearch={(val) => {
                                    setUserSearchValue(val)
                                    handleUserSearch(val)
                                }}
                                autoClearSearchValue={false}
                                onPopupScroll={handleUserScroll}
                                notFoundContent={
                                    getUserStatus === 'loading' &&
                                    clientModalUserPage === 1 ? (
                                        <Icon
                                            icon="line-md:loading-loop"
                                            style={{
                                                fontSize: 24,
                                                color: '#6366f1',
                                            }}
                                        />
                                    ) : null
                                }
                                loading={
                                    getUserStatus === 'loading' &&
                                    clientModalUserPage === 1
                                }
                                onChange={(selectedValues, selectedOptions) => {
                                    setUserSearchValue('')
                                    handleUserSearch('')
                                    setInviteEmail(selectedValues)
                                    const updatedMap = { ...selectedMap }
                                    if (
                                        selectedOptions &&
                                        selectedOptions.length > 0
                                    ) {
                                        selectedOptions.forEach((sel) => {
                                            if (!updatedMap[sel.value]) {
                                                const match = userOptions.find(
                                                    (opt) =>
                                                        opt.value === sel.value
                                                )
                                                if (match)
                                                    updatedMap[sel.value] =
                                                        match
                                            }
                                        })
                                    } else {
                                        // Fallback: find from userOptions based on selectedValues
                                        selectedValues.forEach((val) => {
                                            if (!updatedMap[val]) {
                                                const match = userOptions.find(
                                                    (opt) => opt.value === val
                                                )
                                                if (match)
                                                    updatedMap[val] = match
                                            }
                                        })
                                    }
                                    // Remove unselected items
                                    Object.keys(updatedMap).forEach((key) => {
                                        if (!selectedValues.includes(key)) {
                                            delete updatedMap[key]
                                        }
                                    })
                                    setSelectedMap(updatedMap)
                                }}
                                tagRender={(props) => {
                                    const {
                                        closable,
                                        onClose,
                                        value: val,
                                    } = props
                                    const selectedUser = selectedMap[val]
                                    const memberColor = getMemberColor(
                                        selectedUser?.textLabel
                                    )
                                    const initialsBgColor = hexToRgba(
                                        memberColor,
                                        0.3
                                    )
                                    const initials = getInitials(
                                        selectedUser?.textLabel
                                    )

                                    return (
                                        <div className={styles.selectedUserTag}>
                                            {selectedUser?.profilePicture ? (
                                                <Avatar
                                                    size={18}
                                                    src={
                                                        selectedUser?.profilePicture
                                                    }
                                                    className={
                                                        styles.selectedUserAvatar
                                                    }
                                                />
                                            ) : (
                                                <div
                                                    className={
                                                        styles.selectedUserInitials
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            initialsBgColor,
                                                    }}
                                                >
                                                    {initials}
                                                </div>
                                            )}
                                            <span
                                                className={
                                                    styles.selectedUserName
                                                }
                                            >
                                                {selectedUser?.textLabel}
                                            </span>
                                            {closable && (
                                                <span
                                                    onClick={onClose}
                                                    className={
                                                        styles.selectedUserClose
                                                    }
                                                >
                                                    <CrossIcon
                                                        width={8}
                                                        color={'#64748b'}
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    )
                                }}
                                options={userOptions}
                                className={styles.inviteSelect}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        {getUserStatus === 'loading' &&
                                            clientModalUserPage > 1 && (
                                                <div
                                                    className={
                                                        styles.loadingMore
                                                    }
                                                >
                                                    <Icon
                                                        icon="line-md:loading-loop"
                                                        style={{
                                                            fontSize: 24,
                                                            color: '#6366f1',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                            />
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleInvite}
                                disabled={
                                    !inviteEmail || inviteEmail?.length === 0
                                }
                                loading={isInviting}
                                className={styles.inviteButton}
                            >
                                Invite
                            </Button>
                        </div>

                        {/* Members List */}
                        <div className={styles.membersListSection}>
                            <Text className={styles.sectionTitle}>Members</Text>
                            <div className={styles.membersListContainer}>
                                {projectDetail?.members &&
                                projectDetail?.members?.length > 0 ? (
                                    projectDetail?.members?.map((member) => {
                                        const memberColor = getMemberColor(
                                            member?.userName
                                        )
                                        const initials = getInitials(
                                            member?.userName
                                        )

                                        return (
                                            <div
                                                key={member?._id}
                                                className={
                                                    styles.memberListItem
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.memberListInfo
                                                    }
                                                >
                                                    <Avatar
                                                        size={36}
                                                        className={
                                                            styles.memberListAvatar
                                                        }
                                                        src={
                                                            member?.profilePicture
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                memberColor,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {initials || (
                                                            <Icon
                                                                icon="mdi:account"
                                                                style={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        )}
                                                    </Avatar>
                                                    <div
                                                        className={
                                                            styles.memberListDetails
                                                        }
                                                    >
                                                        <Text
                                                            className={
                                                                styles.memberListName
                                                            }
                                                        >
                                                            {member?.userName ||
                                                                'Unnamed Member'}
                                                        </Text>
                                                        {member?.email && (
                                                            <Text
                                                                className={
                                                                    styles.memberListEmail
                                                                }
                                                            >
                                                                {member?.email}
                                                            </Text>
                                                        )}
                                                    </div>
                                                </div>
                                                {!member?.isSuperAdmin &&
                                                    userProfile?._id !==
                                                        member?._id && (
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <Icon
                                                                    icon="mdi:exit-to-app"
                                                                    style={{
                                                                        fontSize: 18,
                                                                    }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                handleLeaveProject(
                                                                    member?._id,
                                                                    member?.userName
                                                                )
                                                            }
                                                            className={
                                                                styles.leaveProjectButton
                                                            }
                                                        >
                                                            Remove from project
                                                        </Button>
                                                    )}
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className={styles.noMembers}>
                                        <Icon
                                            icon="mdi:account-group-outline"
                                            className={styles.noMembersIcon}
                                        />
                                        <Text>No members yet</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>

                {/* Import Tasks Modal */}
                <ImportTasksModal
                    open={importModalVisible}
                    onCancel={() => setImportModalVisible(false)}
                    projectId={projectId}
                    onImportSuccess={() => {
                        // Refresh task list after successful import
                        dispatch(
                            taskListSupport({
                                skip: 0,
                                limit: 10,
                                filter: {
                                    projectId,
                                },
                                listType: 'status',
                            })
                        )
                    }}
                />

                <CreateClientModal
                    open={isClientModalOpen}
                    onCancel={handleClientModalClose}
                    onSaved={handleClientModalSaved}
                    mode={clientModalMode}
                    initialClient={editingClient}
                    fallbackProjects={clientModalFallbackProjects}
                />

                <CreateProjectModal
                    open={editProjectOpen}
                    onCancel={() => setEditProjectOpen(false)}
                    mode="edit"
                    projectId={projectDetail?._id || projectId}
                    initialProject={projectDetail}
                    onSaved={() => {
                        setEditProjectOpen(false)
                        dispatch(fetchProjectDetail({ id: projectId }))
                    }}
                />
            </div>
        </ProjectPermissionsContext.Provider>
    )
}

export default ProjectDetail
